import toFixed from 'accounting-js/lib/toFixed.js';
import i18n from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { forkJoin } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import * as yup from 'yup';
import { TabKey, TabWorkMap } from '..';

import Form, { SubmitType } from '../../../../components/form';
import Amount from '../../../../components/form/elements/amount';
import Button from '../../../../components/form/elements/button';
import Date from '../../../../components/form/elements/date';
import Disabled from '../../../../components/form/elements/disabled';
import Link from '../../../../components/form/elements/link';
import Percentage from '../../../../components/form/elements/percentage';
import Readonly from '../../../../components/form/elements/readonly';
import Select, {
  emptySelectOption,
  Option,
} from '../../../../components/form/elements/select';
import Show from '../../../../components/form/elements/show';
import Text from '../../../../components/form/elements/text';
import Watch, { WatchType } from '../../../../components/form/elements/watch';
import Classifier from '../../../../enums/classifier';
import ClassifierGroup from '../../../../enums/classifier-group';
import ClassifierPropertyKey from '../../../../enums/classifier-property-key';
import { CropWorkType } from '../../../../enums/crop-work-types';
import Cargo from '../../../../interfaces/cargo';
import ClassifierValue from '../../../../interfaces/classifierValue';
import Field from '../../../../interfaces/field';
import Paginate from '../../../../interfaces/paginate';
import Warehouse from '../../../../interfaces/warehouse';
import Work from '../../../../interfaces/work';
import {
  CROP_YIELD_ACCOUNTING_INDEX,
  CROP_YIELD_ACCOUNTING_CARGO_INDEX,
  CROP_YIELD_ACCOUNTING_CARGO_VIEW,
  WAREHOUSE_MOVEMENT_INDEX,
} from '../../../../router/routes';
import { useService } from '../../../../services/services';
import { numberTransform, route, routeQuery } from '../../../../services/utils';
import WarehouseSelect from './warehouse-select';
import { getCountableUnitsByUnit } from '../../../../enums/unit';
import OptionModal from '../../../../components/modals/option-modal';
import css from '../../../warehouse/movement/index.module.css';
import TabsKeys from '../../../../enums/tab-keys';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

interface FormData {
  id: string;
  work: Work;
  work_type_name: string;
  field: Field;
  product_type: Option & { units: string[]; base_humidity: string };
  gross: number;
  gross_unit: Option;
  tare: number;
  tare_unit: Option;
  net: number;
  net_unit: Option;
  impurities: number;
  impurities_total: number;
  impurities_total_unit: Option;
  base_humidity: number;
  humidity: number;
  humidity_above_norm: number;
  humidity_above_norm_unit: Option;
  quantity: number;
  quantity_unit: Option;
  warehouses: {
    warehouse: Option;
    quantity: number;
    unit: Option;
  }[];
}

const schema = yup
  .object()
  .shape({
    product_type: yup.object().required().shape({
      value: yup.string().uuid().required(),
    }),
    gross: yup.number().positive().decimal().required(),
    gross_unit: yup.object().required().shape({
      value: yup.string().required(),
    }),
    tare: yup.number().decimal().nullable().transform(numberTransform),
    net: yup.number().positive().decimal().required(),
    impurities: yup.number().percentage().nullable().transform(numberTransform),
    base_humidity: yup
      .number()
      .percentage()
      .nullable()
      .transform(numberTransform),
    humidity: yup.number().percentage().nullable().transform(numberTransform),
    humidity_above_norm: yup
      .number()
      .decimal()
      .nullable()
      .transform(numberTransform),
    quantity: yup.number().positive().decimal(8, 3).required(),
    warehouses: yup.lazy(() => {
      return yup.array().of(
        yup.object().shape({
          quantity: yup.number().positive().decimal(8, 3).required().positive(),
          warehouse: yup.object().required().shape({
            value: yup.string().uuid(),
          }),
        })
      );
    }),
  })
  .test({
    test: (value, context) => {
      const quantity = value?.quantity || 0;
      const warehouseQuantity = ((value?.warehouses as any[]) || [])
        .map((w) => w?.quantity || 0)
        .reduce((a, b) => a + b, 0);
      if (quantity === warehouseQuantity) {
        return true;
      } else {
        return context.createError({
          path: 'warehouses.0.quantity',
          message: i18n.t(
            'pages.crop.yield_accounting.cargo.quantity_mismatch'
          ),
        });
      }
    },
  });
export default function YieldAccountingCargoCreate() {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.yield_accounting.cargo.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const history = useHistory();
  const { farmId, season, yieldId, id } = useParams<{
    farmId: string;
    season: string;
    yieldId: string;
    id: string;
  }>();
  const {
    workService,
    fieldService,
    classifierService,
    warehouseService,
    cargoService,
  } = useService();

  const formMethods = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const [formData, setFormData] = useState<FormData>(null);

  useEffect(() => {
    if (id) {
      cargoService.show(farmId, yieldId, id).subscribe((cargo) =>
        setFormData((state) => ({
          ...state,
          id: cargo.id,
          work: cargo.work,
          work_type_name: t(
            'classifiers.properties.crop_work_type.'.concat(cargo.work.type_id)
          ),
          field: {
            ...cargo.field,
            crop_id: cargo.workField.crop_id,
            crop_value: cargo.workField.crop_value,
            breed: cargo.workField.breed,
          },
          product_type: {
            value: cargo.product_type_id,
            label: cargo.product_type_value,
            units: Object.values(cargo.product_type.properties).find(
              (p) => p.property.key === ClassifierPropertyKey.UNITS_OF_MEASURE
            )?.value,
            base_humidity: Object.values(cargo.product_type.properties).find(
              (p) => p.property.key === ClassifierPropertyKey.BASE_HUMIDITY
            )?.value,
          },
          gross: cargo.gross,
          gross_unit: { value: cargo.unit, label: cargo.unit },
          tare: cargo.tare,
          tare_unit: { value: cargo.unit, label: cargo.unit },
          net: cargo.net,
          net_unit: { value: cargo.unit, label: cargo.unit },
          impurities: cargo.impurities,
          impurities_total: cargo.impurities_total,
          impurities_total_unit: { value: cargo.unit, label: cargo.unit },
          base_humidity: cargo.base_humidity,
          humidity: cargo.humidity,
          humidity_above_norm: cargo.humidity_above_norm,
          humidity_above_norm_unit: { value: cargo.unit, label: cargo.unit },
          quantity: cargo.quantity,
          quantity_unit: { value: cargo.unit, label: cargo.unit },
          warehouses: cargo.cargo_warehouses.map((cw) => ({
            warehouse: {
              value: cw.warehouse.id,
              label: cw.warehouse.name,
            },
            quantity: cw.quantity,
            unit: {
              value: cw.unit,
              label: cw.unit,
            },
          })),
        }))
      );
    } else {
      forkJoin({
        work: workService.show(farmId, yieldId.split('|')[0]),
        field: fieldService.show(farmId, yieldId.split('|')[1]),
      }).subscribe((response) => {
        if (!response.work?.done) {
          history.push(
            route(CROP_YIELD_ACCOUNTING_INDEX, {
              farmId: farmId,
              season: season,
              tab: Object.keys(TabWorkMap).find(
                (tab) => TabWorkMap[tab as TabKey] === response.work.type_id
              ),
            })
          );
        }

        const field: any = response.work.fields.find(
          (f: any) => f.id === response.field.id
        );
        const crop: any = response.work.crops?.find(
          (crop: any) => crop.id === field.pivot.crop_id
        );

        return setFormData((state) => ({
          ...state,
          farm_id: farmId,
          work: response.work,
          work_type_name: i18n.t(
            'classifiers.properties.crop_work_type.'.concat(
              response.work.type_id
            )
          ),

          field: {
            ...response.field,
            crop_id: crop?.id,
            crop_value: crop?.value,
            breed: field.pivot.breed,
          },
          product_type: { ...emptySelectOption, units: [], base_humidity: '' },
        }));
      });
    }
  }, [
    t,
    id,
    yieldId,
    farmId,
    cargoService,
    fieldService,
    workService,
    history,
    season,
  ]);

  const submit = useCallback<SubmitType<FormData, Cargo>>(
    (form) => {
      const cargo = {
        id: form.id,
        work_id: form.work.id,
        field_id: form.field.id,
        product_type_id: form.product_type?.value,
        unit: form.gross_unit?.value,
        gross: form.gross,
        tare: form.tare || 0,
        net: form.net,
        impurities: form.impurities || 0,
        impurities_total: form.impurities_total || 0,
        base_humidity: form.base_humidity || 0,
        humidity: form.humidity || 0,
        humidity_above_norm: form.humidity_above_norm || 0,
        quantity: form.quantity,
        cargo_warehouses: form.warehouses
          .filter((w) => w.warehouse)
          .map((w) => {
            return {
              warehouse_id: w.warehouse.value,
              quantity: w.quantity,
              unit: form.gross_unit?.value,
            };
          }),
      } as Cargo;
      if (id) {
        return [
          cargoService.update(farmId, yieldId, id, cargo),
          (response) =>
            history.push(
              route(CROP_YIELD_ACCOUNTING_CARGO_VIEW, {
                farmId: farmId,
                season: season,
                yieldId,
                id: response.id,
              })
            ),
        ];
      } else {
        return [
          cargoService.store(farmId, yieldId, cargo),
          () =>
            history.push(
              route(CROP_YIELD_ACCOUNTING_CARGO_INDEX, {
                farmId: farmId,
                season: season,
                yield_id: yieldId,
              })
            ),
        ];
      }
    },
    [cargoService, farmId, season, yieldId, id, history]
  );

  const calcNet = (watch: WatchType) => {
    const gross = parseFloat(watch.getValues('gross'));
    const tare = parseFloat(watch.getValues('tare')) || 0;
    if (isNaN(gross)) {
      watch.setValue('net', '', { shouldDirty: true });
    } else {
      watch.setValue('net', toFixed(gross - tare, 2), { shouldDirty: true });
    }
  };

  const calcImpuritiesTotal = (watch: WatchType) => {
    const net = parseFloat(watch.getValues('net'));
    const impurities = parseFloat(watch.getValues('impurities'));
    if (isNaN(net) || isNaN(impurities)) {
      watch.setValue('impurities_total', '', { shouldDirty: true });
    } else {
      watch.setValue('impurities_total', toFixed((net * impurities) / 100, 2), {
        shouldDirty: true,
      });
    }
  };

  const calcHumidityAboveNorm = (watch: WatchType) => {
    const baseHumidity = parseFloat(watch.getValues('base_humidity'));
    const humidity = parseFloat(watch.getValues('humidity'));
    const net = parseFloat(watch.getValues('net'));
    const impuritiesTotal = parseFloat(watch.getValues('impurities_total'));
    if (
      isNaN(baseHumidity) ||
      isNaN(humidity) ||
      isNaN(net) ||
      isNaN(impuritiesTotal)
    ) {
      watch.setValue('humidity_above_norm', '', { shouldDirty: true });
    } else if (humidity <= baseHumidity) {
      watch.setValue('humidity_above_norm', '0', { shouldDirty: true });
    } else {
      watch.setValue(
        'humidity_above_norm',
        toFixed(
          ((net - impuritiesTotal) * (humidity - baseHumidity)) /
            (100 - baseHumidity),
          2
        ),
        { shouldDirty: true }
      );
    }
  };

  const calcQuantity = (watch: WatchType) => {
    const workTypeId = watch.getValues('work.type_id');
    const net = parseFloat(watch.getValues('net'));
    const impuritiesTotal =
      parseFloat(watch.getValues('impurities_total')) || 0;
    const humidityAboveNorm =
      parseFloat(watch.getValues('humidity_above_norm')) || 0;
    switch (true) {
      case isNaN(net):
        watch.setValue('quantity', '', { shouldDirty: true });
        break;
      case workTypeId !== CropWorkType.THRESHING:
        watch.setValue('quantity', net, { shouldDirty: true });
        break;
      default:
        watch.setValue(
          'quantity',
          toFixed(net - impuritiesTotal - humidityAboveNorm, 2),
          { shouldDirty: true }
        );
        break;
    }
  };

  const getAllowedUnits = (getValues: any) => {
    const existingCargos = getValues('work.cargos')?.filter(
      (cargo: any) =>
        cargo.product_type_id === getValues('product_type').value &&
        cargo.id !== getValues('id') &&
        cargo.field_id === getValues('field').id
    );

    let units = getValues('product_type')?.units;
    if (existingCargos?.length > 0) {
      units = getCountableUnitsByUnit(existingCargos[0].unit, units);
    }
    return (
      units?.map((u: string) => ({
        label: u,
        value: u,
      })) ?? []
    );
  };

  const [body] = useState(() => [
    Readonly([
      Text('work_type_name', tp('work_type_name')),
      Date('work.date', tp('work_date')),
      Text('field.crop_value', t('pages.crop.fields_and_works.fields.crop')),
      Text('field.breed', t('pages.crop.fields_and_works.fields.breed')),
    ]),
    Watch(
      Select('product_type', {
        asyncOptions: (value, loadedOptions, { page, getValues }) => {
          if (getValues('field.crop_id')) {
            return classifierService
              .valuesPaged(ClassifierGroup.WAREHOUSE, Classifier.PRODUCT_TYPES, {
                page,
                value,
                parent_id: getValues('field.crop_id'),
              })
              .pipe(
                map((response: Paginate<ClassifierValue>) => ({
                  hasMore: response.last_page > response.current_page,
                  options: response.data.map((cv) => ({
                    label: cv.value,
                    value: cv.id,
                    units: Object.values(cv.properties).find(
                      (p) =>
                        p.property.key === ClassifierPropertyKey.UNITS_OF_MEASURE
                    )?.value,
                    base_humidity: Object.values(cv.properties).find(
                      (p) =>
                        p.property.key === ClassifierPropertyKey.BASE_HUMIDITY
                    )?.value,
                  })),
                  additional: { page: response.current_page + 1 },
                }))
              )
          }
        },
        label: tp('product_type'),
        clearable: false,
      }),
      (w) => {
        if (w.isDirty) {
          const unitValue = w.getValues('gross_unit')?.value;
          const units = w.value?.units;

          if (units && unitValue && !units.includes(unitValue)) {
            w.setValue('gross_unit', null);
          }

          const baseHumidity = parseFloat(w.value.base_humidity);
          if (isNaN(baseHumidity)) {
            w.setValue('base_humidity', '', { shouldDirty: true });
          } else {
            w.setValue('base_humidity', w.value.base_humidity, {
              shouldDirty: true,
            });
          }
        }
      }
    ),
    Watch(
      Amount(
        { amount: 'gross', unit: 'gross_unit' },
        {
          asyncOptions: async (value, loadedOptions, { getValues }) => ({
            hasMore: false,
            options: getAllowedUnits(getValues),
            additional: {},
          }),
          cacheOn: ['product_type.value'],
          label: tp('gross'),
        }
      ),
      (w) => {
        w.setValue('tare_unit', w.value[1]);
        w.setValue('net_unit', w.value[1]);
        w.setValue('impurities_total_unit', w.value[1]);
        w.setValue('humidity_above_norm_unit', w.value[1]);
        w.setValue('quantity_unit', w.value[1]);
        Object.keys(w.getValues('warehouses')).forEach((key) =>
          w.setValue('warehouses.'.concat(key, '.unit'), w.value[1])
        );
        if (w.isDirty) {
          calcNet(w);
          calcQuantity(w);
        }
      }
    ),
    Disabled(
      Watch(
        Amount(
          { amount: 'tare', unit: 'tare_unit' },
          {
            asyncOptions: async (value, loadedOptions, { getValues }) => ({
              hasMore: false,
              options:
                getValues('product_type')?.units?.map((u: string) => ({
                  label: u,
                  value: u,
                })) ?? [],
              additional: {},
            }),
            onDisabled: 'unit',
            cacheOn: ['product_type.value'],
            label: tp('tare'),
          }
        ),
        (w) => {
          if (w.isDirty) {
            calcNet(w);
            calcQuantity(w);
          }
        }
      )
    ),
    Show(
      [
        Disabled(
          Watch(
            Amount(
              { amount: 'net', unit: 'net_unit' },
              {
                asyncOptions: async (value, loadedOptions, { getValues }) => ({
                  hasMore: false,
                  options:
                    getValues('product_type')?.units?.map((u: string) => ({
                      label: u,
                      value: u,
                    })) ?? [],
                  additional: {},
                }),
                onDisabled: 'unit',
                cacheOn: ['product_type.value'],
                label: tp('net'),
              }
            ),
            (w) => {
              if (w.isDirty) {
                calcImpuritiesTotal(w);
                calcHumidityAboveNorm(w);
                calcQuantity(w);
              }
            }
          )
        ),
        Watch(Percentage('impurities', tp('impurities')), (w) =>
          calcImpuritiesTotal(w)
        ),
        Show(
          Disabled(
            Watch(
              Amount(
                { amount: 'impurities_total', unit: 'impurities_total_unit' },
                {
                  asyncOptions: async (
                    value,
                    loadedOptions,
                    { getValues }
                  ) => ({
                    hasMore: false,
                    options:
                      getValues('product_type')?.units?.map((u: string) => ({
                        label: u,
                        value: u,
                      })) ?? [],
                    additional: {},
                  }),
                  onDisabled: 'unit',
                  cacheOn: ['product_type.value'],
                  label: tp('impurities_total'),
                }
              ),
              (w) => {
                if (w.isDirty) {
                  calcHumidityAboveNorm(w);
                  calcQuantity(w);
                }
              }
            )
          ),
          ['net', 'impurities'],
          (wv: string[]) => !(wv.includes(undefined) || wv.includes(''))
        ),
        Watch(Percentage('base_humidity', tp('base_humidity')), (w) => {
          if (w.isDirty) {
            calcHumidityAboveNorm(w);
          }
        }),
        Watch(Percentage('humidity', tp('humidity')), (w) => {
          if (w.isDirty) {
            calcHumidityAboveNorm(w);
          }
        }),
        Disabled(
          Watch(
            Amount(
              {
                amount: 'humidity_above_norm',
                unit: 'humidity_above_norm_unit',
              },
              {
                asyncOptions: async (value, loadedOptions, { getValues }) => ({
                  hasMore: false,
                  options:
                    getValues('product_type')?.units?.map((u: string) => ({
                      label: u,
                      value: u,
                    })) ?? [],
                  additional: {},
                }),
                onDisabled: 'unit',
                cacheOn: ['product_type.value'],
                label: tp('humidity_above_norm'),
              }
            ),
            (w) => {
              if (w.isDirty) {
                calcQuantity(w);
              }
            }
          )
        ),
      ],
      'work.type_id',
      (v) => v === CropWorkType.THRESHING
    ),
    Disabled(
      Watch(
        Amount(
          { amount: 'quantity', unit: 'quantity_unit' },
          {
            asyncOptions: async (value, loadedOptions, { getValues }) => ({
              hasMore: false,
              options:
                getValues('product_type')?.units?.map((u: string) => ({
                  label: u,
                  value: u,
                })) ?? [],
              additional: {},
            }),
            onDisabled: 'unit',
            cacheOn: ['product_type.value'],
            label: tp('quantity'),
          }
        ),
        (w) => {
          if (!w.value[0]) {
            return;
          }
          // can't know the keys of warehouse fields, so looping through first few, to see if there are any
          let otherWarehouses = false;
          for (let i = 1; i <= 10; i++) {
            if (
              w.getValues('warehouses.'.concat(i.toString(), '.quantity')) !==
              undefined
            ) {
              otherWarehouses = true;
              break;
            }
          }
          if (otherWarehouses) {
            // if there are multiple warehouse fields, then just force validation
            w.setValue(
              'warehouses.0.quantity',
              w.getValues('warehouses.0.quantity'),
              {
                shouldValidate: true,
              }
            );
          } else {
            // otherwise set value of the first (and only) to equal this
            w.setValue('warehouses.0.quantity', w.value[0], {
              shouldValidate: true,
            });
          }
        }
      )
    ),
    WarehouseSelect(
      () => [
        Watch(
          Select('warehouse', {
            asyncOptions: (
              value,
              loadedOptions,
              { page, setValue, parentName }
            ) =>
              warehouseService.index(farmId, { value, page }).pipe(
                tap((response: Paginate<Warehouse>) => {
                  if (response.data.length === 1) {
                    setValue(parentName + '.warehouse', {
                      label: response.data[0].name,
                      value: response.data[0].id,
                    });
                  }
                }),
                map((response: Paginate<Warehouse>) => {
                  return {
                    hasMore: response.last_page > response.current_page,
                    options: response.data.map((w) => ({
                      label: w.name,
                      value: w.id,
                    })),
                    additional: { page: response.current_page + 1 },
                  };
                })
              ),
            label: tp('warehouse'),
            clearable: false,
          }),
          (w) =>
            w.setValue(w.parentName.concat('.unit'), w.getValues('gross_unit'))
        ),
        Disabled(
          Watch(
            Amount(
              { amount: 'quantity', unit: 'unit' },
              {
                asyncOptions: async (value, loadedOptions, { getValues }) => ({
                  hasMore: false,
                  options:
                    getValues('product_type')?.units?.map((u: string) => ({
                      label: u,
                      value: u,
                    })) ?? [],
                  additional: {},
                }),
                onDisabled: 'unit',
                cacheOn: ['product_type.value'],
                label: tp('warehouse_quantity'),
              }
            ),
            (w) => {
              // force validation to display the error message on the first warehouse
              if (w.parentName !== 'warehouses.0') {
                w.setValue(
                  'warehouses.0.quantity',
                  w.getValues('warehouses.0.quantity'),
                  {
                    shouldValidate: true,
                  }
                );
              }
            }
          )
        ),
      ],
      'warehouses',
      tp('add_warehouse'),
      tp('add_warehouse')
    ),
  ]);

  const [footer] = useState(() => {
    if (id) {
      return [
        Button('save'),
        Link(
          route(CROP_YIELD_ACCOUNTING_CARGO_VIEW, {
            farmId: farmId,
            season: season,
            yieldId,
            id: id,
          }),
          'cancel'
        ),
      ];
    } else {
      return [
        Button('create'),
        Link(
          route(CROP_YIELD_ACCOUNTING_CARGO_INDEX, {
            farmId: farmId,
            season: season,
            yield_id: yieldId,
          }),
          'cancel'
        ),
      ];
    }
  });

  if (!formData) {
    return null;
  }

  const updateBlockErrorMessage = formMethods.getFieldState(
    'deal_product_ids_blocking_update'
  )?.error?.message;

  const updateBlockModal = () => {
    return (
      updateBlockErrorMessage && (
        <OptionModal
          title={tp('relationship_deals_title_edit')}
          className={css['delete-modal_wrapper']}
          content={
            <div className={css['modal-delete_description']}>
              {tp('description_move_to_deals_edit')}
            </div>
          }
          buttons={[
            {
              title: tp('move_to_deals'),
              name: 'yes',
              value: true,
              ignoreValidation: true,
            },
            {
              title: t('cancel'),
              name: 'no',
              value: false,
              ignoreValidation: true,
            },
          ]}
          onChoice={(choice) => {
            if (choice) {
              sessionStorage.removeItem('current_index_page');
              history.push(
                routeQuery(
                  route(WAREHOUSE_MOVEMENT_INDEX, {
                    farmId: farmId,
                    tab: TabsKeys.deals,
                  }),
                  {
                    ids: updateBlockErrorMessage.split(','),
                  }
                )
              );
            }
            formMethods.clearErrors('deal_product_ids_blocking_update');
          }}
          onClose={() =>
            formMethods.clearErrors('deal_product_ids_blocking_update')
          }
        />
      )
    );
  };

  return (
    <>
      <Form
        mode={id ? 'edit' : 'create'}
        title={`${formData.field.label}`}
        body={body}
        footer={footer}
        submit={submit}
        values={formData}
        schema={schema}
        formMethods={formMethods}
      />
      {updateBlockModal()}
    </>
  );
}
